/**
 * VideoBubble — per-type subcomponent for `ChatMessageType.video` messages.
 *
 * Flutter source of truth:
 *  - Own variant: `currentUserMessageTypeVideo` (`chat_screen.dart:1663-1773`).
 *  - Peer variant: `peerUserMessageTypeVideo` (`chat_screen.dart:2764-2874`).
 *
 * Structurally identical to `ImageBubble` (same sizing rule, same
 * placeholder behavior, same one-sided padding) — the only difference is a
 * centered play-icon overlay rendered on top of the thumbnail
 * (`chat_screen.dart:1756-1767` own / `:2856-2867` peer:
 *   `Icon(Icons.play_arrow, color: AppColors.white.withValues(alpha: .7),
 *    size: 40)`).
 *
 * Per Task 27's plan directive ("extract the shared part into a tiny
 * `MediaBubbleFrame` if >50% of the layout is shared"), the shell lives in
 * `MediaBubbleFrame`. This component resolves the thumbnail URL, stubs the
 * tap, and passes the play overlay as `children`.
 *
 * Tap routing (Task 15 / consideration J): a READY video bubble taps to the
 * directly-routable `MediaDetailsPage` (`/chat/media/:id`) via
 * `Navigator.navigateToMediaDetails`, passing the message + the peer's public
 * JWK as route args; the page initializes the encrypted chat video and plays it
 * inline with native HLS controls (`InlineVideoPlayer`). A bubble that is still
 * uploading (`isUploading`) or failed (`success === false`) shows an `InfoSheet`
 * instead of opening the page — mirroring `UserDetailsPage.handleMediaClick`.
 *
 * Play overlay asset: `assets/vectors/play_filled.svg` — the same asset
 * `CommentMediaThumbnail` uses for its video-thumb overlay
 * (`CommentMediaThumbnail.tsx:94`). The plan said to "match what
 * `ContentCard` uses"; `ContentCard` does not exist on web, but
 * `CommentMediaThumbnail` is the established play-overlay pattern in the
 * project and lives under the same content-thumbnail family.
 *
 * Play overlay color / size:
 *  - Color → `theme.white` with 70% alpha appended (`B3` hex), exact parity
 *    with Flutter's `AppColors.white.withValues(alpha: .7)`. This is the
 *    same `hex + 'B3'` pattern used elsewhere in the project (the
 *    transparency-on-token pattern; see `ContentComponentOverlays.tsx:336`
 *    which appends `'1A'` for 10% alpha against `theme.white`).
 *  - Size → `responsive.icon.xlarge` (= 32). Flutter spec is 40px; the
 *    project's responsive icon scale has `xlarge` (32) and `xxlarge` (48)
 *    surrounding 40. The same `xlarge` is what `CommentMediaThumbnail`
 *    uses for its play overlay (`CommentMediaThumbnail.tsx:95-96`) — that
 *    decision was already approved on the existing thumb-overlay pattern,
 *    so this component follows it for consistency. The 8px deviation from
 *    Flutter's literal 40 is a "small UI deviation" allowed by
 *    `presentation.md` when a closer-matching token would require adding
 *    one (no `responsive.icon.xl` between 32 and 48 exists).
 *
 * Thumbnail path: same as `ImageBubble` — the encrypted thumbnail bytes are
 * fetched+decrypted by `EncryptedCachedImage`, which now takes the
 * BUCKET-RELATIVE object path and resolves a valid, tokenized Firebase Storage
 * download URL itself via the domain UseCase (Finding 5). So this component
 * hands the frame the raw `message.relativeThumbnailPath` and does NOT prefix
 * the bucket. The `relativeThumbnailPath` field on `ChatMessageEntity` is the
 * same field both image and video messages use.
 */

import React from 'react';
import { ChatMessageType } from '@/domain/enums/chatMessageType';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { InfoSheet } from '@/presentation/components/common/sheet/info/InfoSheet';
import { Navigator } from '@/presentation/routing/Navigator';
import {
  registerBubble,
  type BubbleSubcomponentProps,
} from '../../registry/bubbleRegistry';
import { MediaBubbleFrame } from './MediaBubbleFrame';

export const VideoBubble: React.FC<BubbleSubcomponentProps> = ({
  message,
  peerUser,
  isOwn,
  isLastOfRun,
  onRetry,
  screenKey,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();

  // Same path-resolution rule as `ImageBubble` — bucket-RELATIVE thumbnail
  // object path; `EncryptedCachedImage` resolves the tokenized download URL via
  // the domain UseCase (Finding 5). No `DEFAULT_BUCKET_PREFIX` prefix here.
  const thumbnailPath = message.relativeThumbnailPath ?? '';

  // Defensive uploading branch — same rationale as `ImageBubble`.
  const isUploading = message.isUploading === true;

  // Tap handler (consideration J — mirror `UserDetailsPage.handleMediaClick`):
  //  1. Own + failed → re-upload via `onRetry` (Finding 3, unchanged).
  //  2. Still uploading (`isUploading`) → processing InfoSheet, no navigation.
  //  3. Failed (`success === false`) and NOT own → failed InfoSheet.
  //  4. Otherwise READY → open `MediaDetailsPage` with the message + peer JWK as
  //     route args; the page plays the encrypted chat video inline.
  const handleTap = (): void => {
    if (isOwn && message.success === false) {
      onRetry(message);
      return;
    }
    if (isUploading) {
      void InfoSheet.show({
        title: l10n.chat.mediaDetails.processingTitle,
        infoText: l10n.chat.mediaDetails.processingDescription,
        screenKey,
        qaId: 'media-info-sheet',
      });
      return;
    }
    if (message.success === false) {
      void InfoSheet.show({
        title: l10n.chat.mediaDetails.failedTitle,
        infoText: l10n.chat.mediaDetails.failedDescription,
        screenKey,
        qaId: 'media-info-sheet',
      });
      return;
    }
    Navigator.navigateToMediaDetails(message.documentId, {
      message,
      // Same key the chat already derived for the open conversation's peer
      // (`useChat.runInitialLoad`); the page re-derives it to decrypt.
      peerPublicJwk: peerUser?.publicJwk ?? null,
    });
  };

  // White at 70% alpha — Flutter `AppColors.white.withValues(alpha: .7)`.
  // `'B3'` is the hex form of `Math.round(0.7 * 255) = 179`.
  const playOverlayColor = `${theme.white}B3`;

  return (
    <MediaBubbleFrame
      isOwn={isOwn}
      isLastOfRun={isLastOfRun}
      aspectRatio={message.aspectRatio}
      thumbnailSource={thumbnailPath}
      // The video thumbnail bytes are E2EE ciphertext; the peer's public JWK
      // drives the chat-key decrypt inside `EncryptedCachedImage`. Same key the
      // chat already derived at load time (`useChat.runInitialLoad`).
      peerPublicJwk={peerUser?.publicJwk ?? null}
      isUploading={isUploading}
      processingLabel={l10n.chat.uploading.video}
      onTap={handleTap}
      qa={{ id: `chat-bubble-video-${message.documentId}` }}
    >
      {/* Play overlay — shown for every non-uploading video, INCLUDING the
          no-thumbnail case (consideration D: a video whose thumbnail failed to
          generate / decrypt should still render the placeholder surface + play
          icon so the tile reads as a playable video, not a broken image). Only
          the uploading branch suppresses the overlay — it shows the
          "Processing video" placeholder text instead. The frame paints the
          mode-aware `theme.secondary` placeholder behind the icon when there is
          no thumbnail, so the play icon never floats over a broken image. */}
      {!isUploading && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: responsive.borderRadius.round,
            backgroundColor: theme.loadingOverlay,
            padding: responsive.spacing.sm,
          }}
        >
          <AssetIcon
            path="/assets/vectors/play_filled.svg"
            width={responsive.icon.xlarge}
            height={responsive.icon.xlarge}
            color={playOverlayColor}
          />
        </div>
      )}
    </MediaBubbleFrame>
  );
};

// Register with the dispatcher so `MessageBubble` routes
// `ChatMessageType.video` to this component. See `MessageBubble.tsx` for
// the registry contract.
registerBubble(ChatMessageType.video, VideoBubble);
